import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useRef } from "react";
import { Button } from "./ui/Button";

type ModalProps = {
  open: boolean;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Receives the sticky footer element so content can portal actions into it. */
  footerRef?: (el: HTMLDivElement | null) => void;
  /** Tailwind max-width class for the panel. */
  maxWidth?: string;
  /** Centered card (default) or right-edge full-height drawer. */
  variant?: "dialog" | "drawer";
};

const isTopModal = (panel: HTMLElement | null): boolean => {
  if (!panel) return false;
  const panels = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
  return panels[panels.length - 1] === panel;
};

/**
 * Hand-rolled overlay. MUI's `Dialog` + `DialogContent` paints its own white
 * surface that shows as a halo around every themed panel, so the app builds
 * modals directly (see AnalyseProgressModal for the original of this pattern).
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  footerRef,
  maxWidth = "max-w-4xl",
  variant = "dialog",
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTopModal(panelRef.current)) return;
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    // Focus inside the body, not the panel: the close button is first in
    // document order, and landing on it put a focus ring on "dismiss" every
    // time a dialog opened.
    // A press inside the panel before the timer fires means the user already
    // picked a field; focusing the first input then would blur it and close
    // the dropdown they just opened.
    let userPressed = false;
    const onPointerDown = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) userPressed = true;
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    const focusTimer = window.setTimeout(() => {
      if (
        userPressed ||
        panelRef.current?.contains(document.activeElement)
      ) {
        return;
      }
      const bodyFocus = bodyRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex="0"]',
      );
      if (bodyFocus) {
        bodyFocus.focus();
        return;
      }
      panelRef.current
        ?.querySelector<HTMLElement>("footer button:not([disabled])")
        ?.focus();
    }, 30);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
      window.clearTimeout(focusTimer);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const isDrawer = variant === "drawer";

  return (
    <div
      className={
        isDrawer
          ? "fixed inset-0 z-[1300] flex justify-end bg-black/60"
          : "fixed inset-0 z-[1300] flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center sm:p-6"
      }
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={
          isDrawer
            ? `flex h-full w-full ${maxWidth} flex-col overflow-hidden rounded-none border border-od-border bg-od-surface shadow-2xl sm:rounded-l-xl`
            : `flex max-h-[calc(100vh-3rem)] w-full ${maxWidth} flex-col overflow-hidden rounded-xl border border-od-border bg-od-surface shadow-2xl`
        }
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-od-border px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-base font-semibold text-od-text">
              {title}
            </h2>
            {subtitle && (
              <p className="m-0 mt-0.5 truncate text-xs text-od-muted">
                {subtitle}
              </p>
            )}
          </div>
          <Button
            onClick={onClose}
            aria-label="Close"
            variant="ghost"
            size="icon"
            className="-mr-1 -mt-1"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Button>
        </header>

        <div
          ref={bodyRef}
          className={
            isDrawer
              ? "min-h-0 flex-1 overflow-y-auto px-5 py-4"
              : "min-h-0 flex-1 overflow-y-auto px-5 py-4"
          }
        >
          {children}
        </div>

        <footer
          ref={footerRef}
          className="shrink-0 border-t border-od-border bg-od-surface px-5 py-3 empty:hidden empty:border-0 empty:p-0"
        >
          {footer}
        </footer>
      </div>
    </div>
  );
};
